import type { WidgetSettings } from '@shared/api';
import { getDefaultContactFormFields, parseContactFormFields } from './contact-form-fields';
import { BUTTON_THEME_COLORS } from './constants';
import type { PresetFormValues } from './types';
import { WIDGET_TRANSLATIONS, type WidgetLang } from './widget-translations';

const BASE_PRESET_VALUES: PresetFormValues = {
  name: '',
  description: '',
  personal_data_policy_url: null,
  appearance: {
    brand_color: '#234EEE',
    button_theme: 'light',
    button_background_color: BUTTON_THEME_COLORS.light.background,
    button_text_color: BUTTON_THEME_COLORS.light.text,
    widget_icon: {
      type: 'default',
      custom_url: null,
    },
    font_family: 'Inter Tight',
  },
  content: {
    button_text: '',
    assistant_name: '',
    welcome_message: '',
    quick_questions: [],
  },
  preview: {
    mode: 'dynamic',
    show_delay_seconds: 6,
    static_question: null,
    static_answers: [],
  },
  analytics: {
    yandex_metrika: {
      enabled: false,
      counter_id: null,
      goals: [],
    },
  },
  advanced: {
    contact_form: {
      mode: 'by_events',
      message_before_form: '',
      show_after_messages: 1,
      show_close_button: false,
      show_form_after_answer: false,
      trigger_events: ['SHOW_FORM'],
      fields: [],
    },
    messages: {
      border_radius: 20,
      tail_enabled: false,
      show_agent_answer_hints: true,
    },
    widget: {
      width_px: 497,
      height_px: 763,
      border_width_px: 0,
      border_color: '#C8CAD0',
    },
    button: {
      border_radius: 46,
      width_px: 175,
      height_px: 46,
      border_width_px: 0,
      border_color: '#C8CAD0',
    },
    preview: {
      message_border_radius: 20,
      hint_border_radius: 40,
      show_agent_answer_hints: true,
    },
    position: {
      desktop: {
        placement: 'bottom_right',
        side_offset: 30,
        bottom_offset: 30,
        unit: 'px',
      },
      mobile: {
        placement: 'bottom_right',
        side_offset: 30,
        bottom_offset: 65,
        unit: 'px',
      },
    },
  },
};

export const getDefaultPresetValues = (lang: WidgetLang = 'ru'): PresetFormValues => {
  const translations = WIDGET_TRANSLATIONS[lang];
  const values = structuredClone(BASE_PRESET_VALUES);

  values.content.button_text = translations.triggerLabel;
  values.content.assistant_name = translations.welcomeAssistantName;
  values.content.welcome_message = translations.defaultFirstMessage;
  values.content.quick_questions = [...translations.fallbackRecommendations];
  values.advanced.contact_form.message_before_form = translations.authPromptMessage;
  values.advanced.contact_form.fields = getDefaultContactFormFields(translations);

  return values;
};

export const DEFAULT_PRESET_VALUES = getDefaultPresetValues();

const emptyToNull = (value?: string | null) => value?.trim() || null;

export const getPresetFormValues = (
  settings?: WidgetSettings,
  lang: WidgetLang = 'ru',
): PresetFormValues => {
  if (!settings) return getDefaultPresetValues(lang);

  const defaults = getDefaultPresetValues(lang);
  const responseContactForm = settings.advanced.contact_form;
  const fallbackFields = defaults.advanced.contact_form.fields;
  const fields =
    responseContactForm.fields === undefined
      ? fallbackFields
      : parseContactFormFields(responseContactForm.fields);

  return {
    name: settings.name,
    description: settings.description,
    personal_data_policy_url: settings.personal_data_policy_url,
    appearance: {
      ...settings.appearance,
      widget_icon: { ...settings.appearance.widget_icon },
    },
    content: {
      ...settings.content,
      quick_questions: [...settings.content.quick_questions],
    },
    preview: {
      ...settings.preview,
      static_answers: [...settings.preview.static_answers],
    },
    analytics: {
      yandex_metrika: {
        ...settings.analytics.yandex_metrika,
        goals: settings.analytics.yandex_metrika.goals.map((goal) => ({ ...goal })),
      },
    },
    advanced: {
      contact_form: {
        ...settings.advanced.contact_form,
        show_close_button: responseContactForm.show_close_button ?? false,
        show_form_after_answer: responseContactForm.show_form_after_answer ?? false,
        trigger_events: [...settings.advanced.contact_form.trigger_events],
        fields: fields.map((field) => ({
          ...field,
          placeholder: field.placeholder ?? null,
          options: [...(field.options ?? [])],
        })),
      },
      messages: { ...settings.advanced.messages },
      widget: {
        width_px: settings.advanced.widget?.width_px ?? defaults.advanced.widget.width_px,
        height_px: settings.advanced.widget?.height_px ?? defaults.advanced.widget.height_px,
        border_width_px:
          settings.advanced.widget?.border_width_px ?? defaults.advanced.widget.border_width_px,
        border_color:
          settings.advanced.widget?.border_color ?? defaults.advanced.widget.border_color,
      },
      button: {
        border_radius: settings.advanced.button.border_radius,
        width_px: settings.advanced.button.width_px ?? defaults.advanced.button.width_px,
        height_px: settings.advanced.button.height_px ?? defaults.advanced.button.height_px,
        border_width_px:
          settings.advanced.button.border_width_px ?? defaults.advanced.button.border_width_px,
        border_color:
          settings.advanced.button.border_color ?? defaults.advanced.button.border_color,
      },
      preview: { ...settings.advanced.preview },
      position: {
        desktop: { ...settings.advanced.position.desktop },
        mobile: { ...settings.advanced.position.mobile },
      },
    },
  };
};

export const normalizePresetValues = (values: PresetFormValues): PresetFormValues => ({
  ...values,
  name: values.name.trim(),
  description: values.description.trim(),
  personal_data_policy_url: emptyToNull(values.personal_data_policy_url),
  appearance: {
    ...values.appearance,
    brand_color: emptyToNull(values.appearance.brand_color),
    button_background_color:
      values.appearance.button_theme === 'custom'
        ? emptyToNull(values.appearance.button_background_color)
        : BUTTON_THEME_COLORS[values.appearance.button_theme].background,
    button_text_color:
      values.appearance.button_theme === 'custom'
        ? emptyToNull(values.appearance.button_text_color)
        : BUTTON_THEME_COLORS[values.appearance.button_theme].text,
    font_family: emptyToNull(values.appearance.font_family),
    widget_icon: {
      ...values.appearance.widget_icon,
      custom_url: emptyToNull(values.appearance.widget_icon.custom_url),
    },
  },
  content: {
    ...values.content,
    button_text: values.content.button_text.trim(),
    assistant_name: values.content.assistant_name.trim(),
    welcome_message: values.content.welcome_message.trim(),
    quick_questions: values.content.quick_questions.map((item) => item.trim()).filter(Boolean),
  },
  preview: {
    ...values.preview,
    static_question: values.content.welcome_message.trim(),
    static_answers: (values.preview.static_answers.some((item) => item.trim())
      ? values.preview.static_answers
      : values.content.quick_questions
    )
      .map((item) => item.trim())
      .filter(Boolean),
  },
  analytics: {
    yandex_metrika: {
      ...values.analytics.yandex_metrika,
      counter_id: emptyToNull(values.analytics.yandex_metrika.counter_id),
      goals: values.analytics.yandex_metrika.goals
        .map((goal) => ({
          event: goal.event.trim(),
          goal_id: goal.goal_id.trim(),
        }))
        .filter((goal) => goal.event && goal.goal_id),
    },
  },
  advanced: {
    ...values.advanced,
    contact_form: {
      ...values.advanced.contact_form,
      show_close_button: values.advanced.contact_form.show_close_button ?? false,
      show_form_after_answer: values.advanced.contact_form.show_form_after_answer ?? false,
      message_before_form: values.advanced.contact_form.message_before_form.trim(),
      trigger_events: values.advanced.contact_form.trigger_events
        .map((item) => item.trim())
        .filter(Boolean),
      fields: values.advanced.contact_form.fields.map((field) => ({
        ...field,
        alias: field.alias.trim().toLowerCase(),
        label: field.label.trim(),
        placeholder: emptyToNull(field.placeholder),
        options:
          field.type === 'select'
            ? field.options.map((option) => option.trim()).filter(Boolean)
            : [],
      })),
    },
  },
});
